// TeamTaskDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  Users,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Calendar,
  Tag,
} from "lucide-react";
import apiService from "../../services/api";
import { toast } from "react-hot-toast";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

const TeamTaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [response, setResponse] = useState("");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadTask();
  }, [id]);

  // Real-time updates
  useRealtimeSync({
    events: ["team_task_notification"],
    onUpdate: (data) => {
      if (data.data.id === id) {
        loadTask();
      }
    },
    enabled: true,
  });

  const loadTask = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTeamTask(id);
      setTask(response.data);
    } catch (error) {
      console.error("Error loading task:", error);
      toast.error("Failed to load task");
      navigate("/team-tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setActionLoading(true);
      await apiService.updateTeamTaskStatus(id, {
        status: "accepted",
      });
      toast.success("Task accepted");
      loadTask();
    } catch (error) {
      toast.error("Failed to accept task");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    const reason = prompt("Please provide a reason for declining:");
    if (!reason) return;

    try {
      setActionLoading(true);
      await apiService.updateTeamTaskStatus(id, {
        status: "declined",
        declineReason: reason,
      });
      toast.success("Task declined");
      loadTask();
    } catch (error) {
      toast.error("Failed to decline task");
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (task.requiresResponse && !response) {
      setShowResponseModal(true);
      return;
    }

    try {
      setActionLoading(true);
      await apiService.updateTeamTaskStatus(id, {
        status: "completed",
        response: response || undefined,
      });
      toast.success("Task completed");
      setShowResponseModal(false);
      setResponse("");
      loadTask();
    } catch (error) {
      toast.error("Failed to complete task");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await apiService.addTeamTaskComment(id, {
        content: newComment,
      });
      setNewComment("");
      loadTask();
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "text-gray-400 bg-gray-400/10 border-gray-500",
      medium: "text-yellow-400 bg-yellow-400/10 border-yellow-500",
      high: "text-orange-400 bg-orange-400/10 border-orange-500",
      urgent: "text-red-400 bg-red-400/10 border-red-500",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-600 text-white",
      accepted: "bg-blue-600 text-white",
      in_progress: "bg-blue-600 text-white",
      completed: "bg-green-600 text-white",
      declined: "bg-red-600 text-white",
      cancelled: "bg-gray-600 text-white",
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0D]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Loading task...
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0D]" : "bg-[#F4F5F7]"}`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Task not found
        </div>
      </div>
    );
  }

  const myAssignment = task.assignments?.find(
    (a) => a.userId === task.myAssignment?.userId,
  );
  const canTakeAction =
    myAssignment && !["completed", "declined"].includes(myAssignment.status);

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 p-3 sm:p-6 ${isDarkMode ? "bg-[#0A0B0D] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
          >
            <ArrowLeft size={20} />
            Go Back
          </button>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1
                className={`text-xl sm:text-3xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {task.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`inline-block px-3 py-1 rounded-lg text-sm font-medium border ${getPriorityColor(
                    task.priority,
                  )}`}
                >
                  {task.priority.toUpperCase()}
                </span>
                {myAssignment && (
                  <span
                    className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(
                      myAssignment.status,
                    )}`}
                  >
                    {myAssignment.status.replace("_", " ").toUpperCase()}
                  </span>
                )}
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Type: {task.type}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Action Buttons */}
            {canTakeAction && (
              <div
                className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Actions
                </h3>
                <div className="flex flex-wrap gap-3">
                  {myAssignment.status === "pending" && (
                    <>
                      <button
                        onClick={handleAccept}
                        disabled={actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        <CheckCircle size={18} />
                        Accept Task
                      </button>
                      <button
                        onClick={handleDecline}
                        disabled={actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        <XCircle size={18} />
                        Decline Task
                      </button>
                    </>
                  )}
                  {["accepted", "in_progress"].includes(
                    myAssignment.status,
                  ) && (
                    <button
                      onClick={handleComplete}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-[#427BCA] hover:bg-[#5A9BE8] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      <CheckCircle size={18} />
                      Mark as Complete
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div
              className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Description
              </h3>
              <p
                className={`whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {task.description || "No description provided"}
              </p>
            </div>

            {/* Response (if required) */}
            {task.requiresResponse && myAssignment?.response && (
              <div
                className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Your Response
                </h3>
                <p
                  className={`whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                >
                  {myAssignment.response}
                </p>
              </div>
            )}

            {/* Comments */}
            <div
              className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <MessageSquare size={20} />
                Comments ({task.comments?.length || 0})
              </h3>

              <div className="space-y-4 mb-4">
                {task.comments && task.comments.length > 0 ? (
                  task.comments.map((comment) => (
                    <div
                      key={comment.id}
                      className={`border-l-2 pl-4 py-2 ${isDarkMode ? "border-[#427BCA]" : "border-blue-500"}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {comment.user?.name}
                        </span>
                        <span
                          className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        >
                          {new Date(comment.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      >
                        {comment.content}
                      </p>
                    </div>
                  ))
                ) : (
                  <p
                    className={`text-center py-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    No comments yet
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                  placeholder="Add a comment..."
                  className={`flex-1 px-4 py-2 rounded-lg focus:outline-none transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]" : "bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-400"}`}
                />
                <button
                  onClick={handleAddComment}
                  className="px-4 py-2 bg-[#427BCA] hover:bg-[#5A9BE8] text-white rounded-lg transition-colors"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Creator */}
            <div
              className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Created By
              </h3>
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${isDarkMode ? "bg-[#427BCA]/20 border border-[#427BCA]/30 text-[#427BCA]" : "bg-blue-100 border border-blue-200 text-blue-600"}`}
                >
                  {task.creator?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .substring(0, 2)}
                </div>
                <div>
                  <div
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {task.creator?.name}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {task.creator?.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div
              className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-sm font-medium mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Details
              </h3>
              <div className="space-y-3">
                {task.dueDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock
                      size={16}
                      className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                    />
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Due:
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {new Date(task.dueDate).toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar
                    size={16}
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  />
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Created:
                  </span>
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {new Date(task.createdAt).toLocaleString()}
                  </span>
                </div>
                {task.tags && task.tags.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <Tag
                      size={16}
                      className={`mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    />
                    <div className="flex flex-wrap gap-1">
                      {task.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Assignees */}
            <div
              className={`rounded-xl border p-4 sm:p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200 shadow-sm"}`}
            >
              <h3
                className={`text-sm font-medium mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Assignees ({task.assignments?.length || 0})
              </h3>
              <div className="space-y-3">
                {task.assignments?.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${isDarkMode ? "bg-[#427BCA]/20 border border-[#427BCA]/30 text-[#427BCA]" : "bg-blue-100 border border-blue-200 text-blue-600"}`}
                      >
                        {assignment.user?.name
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .substring(0, 2)}
                      </div>
                      <span
                        className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {assignment.user?.name}
                      </span>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded ${getStatusColor(
                        assignment.status,
                      )}`}
                    >
                      {assignment.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-xl border max-w-lg w-full p-6 transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200 shadow-lg"}`}
          >
            <h2
              className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Provide Response
            </h2>
            <p
              className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              This task requires a response. Please provide your response before
              completing.
            </p>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              placeholder="Enter your response..."
              className={`w-full px-4 py-2 rounded-lg focus:outline-none mb-4 transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]" : "bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-400"}`}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowResponseModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-[#13161B] border border-gray-700 text-white hover:bg-gray-700" : "bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200"}`}
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={!response.trim() || actionLoading}
                className="flex-1 px-4 py-2 bg-[#427BCA] rounded-lg text-white hover:bg-[#5A9BE8] transition-colors disabled:opacity-50"
              >
                Complete Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamTaskDetail;
